import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/shared";
import { formatMoney, formatNum, can } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { BulkUploadButton } from "../components/BulkUploadButton";

const EMPTY = { name: "", sku: "", warehouse_id: "", unit: "kg", weight: 0, quantity: 0, unit_cost: 0, reorder_level: 0, supplier: "" };

export default function RawMaterials() {
  const { user } = useAuth();
  const editable = can(user.role, "raw");
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [r, w] = await Promise.all([api.get("/raw-materials"), api.get("/warehouses")]);
    setItems(r.data);
    setWarehouses(w.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ ...EMPTY, warehouse_id: warehouses[0]?.id || "" });
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (it) => { setForm(it); setEditing(it.id); setOpen(true); };

  const set = (k, num) => (e) =>
    setForm((f) => ({ ...f, [k]: num ? parseFloat(e.target.value || 0) : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/raw-materials/${editing}`, form);
      else await api.post("/raw-materials", form);
      toast.success(editing ? "Raw material updated" : "Raw material created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this raw material?")) return;
    await api.delete(`/raw-materials/${id}`);
    toast.success("Deleted");
    load();
  };

  const whName = (id) => warehouses.find((w) => w.id === id)?.name || "—";

  return (
    <div data-testid="raw-materials-page">
      <PageHeader
        title="Raw Materials"
        subtitle="Base inventory with weight & quantity tracking"
        action={editable && (
          <div className="flex gap-2">
            <BulkUploadButton uploadUrl="/raw-materials/bulk" templateUrl="/raw-materials/template.csv"
              title="Import Raw Materials"
              description="Upload a .csv or .xlsx with columns: name, sku, unit, quantity, unit_cost, reorder_level, supplier, warehouse."
              onDone={load} testId="raw-import" />
            <Button onClick={openNew} className="rounded-none gap-2 transition-transform duration-200 hover:-translate-y-0.5" data-testid="create-raw-material-btn">
              <Plus className="w-4 h-4" /> New Raw Material
            </Button>
          </div>
        )}
      />

      {items.length === 0 ? (
        <EmptyState title="No raw materials yet" hint="Add your base materials with their weight and quantity to start building products."
          action={editable && <Button onClick={openNew} className="rounded-none gap-2"><Plus className="w-4 h-4" />New Raw Material</Button>} />
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary">
                <TableHead>SKU</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead className="text-right">Weight</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit Cost</TableHead>
                <TableHead className="text-right">Reorder</TableHead>
                {editable && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => {
                const low = it.quantity <= it.reorder_level;
                return (
                  <TableRow key={it.id} className="hover:bg-secondary/60" data-testid="raw-material-row">
                    <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {low && <AlertTriangle className="w-3.5 h-3.5 text-destructive" />}
                        {it.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{whName(it.warehouse_id)}</TableCell>
                    <TableCell className="text-right font-mono">{formatNum(it.weight)} {it.unit}</TableCell>
                    <TableCell className={`text-right font-mono ${low ? "text-destructive font-bold" : ""}`}>{formatNum(it.quantity)}</TableCell>
                    <TableCell className="text-right font-mono">{formatMoney(it.unit_cost, user.organization?.base_currency)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{formatNum(it.reorder_level)}</TableCell>
                    {editable && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none" onClick={() => openEdit(it)} data-testid="edit-raw-material-btn">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-destructive" onClick={() => del(it.id)} data-testid="delete-raw-material-btn">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} Raw Material</DialogTitle><DialogDescription>Track base inventory items with their weight, quantity and cost.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Name</Label>
              <Input className="rounded-none mt-1" value={form.name} onChange={set("name")} required data-testid="raw-name-input" />
            </div>
            <div>
              <Label>SKU</Label>
              <Input className="rounded-none mt-1" value={form.sku} onChange={set("sku")} required data-testid="raw-sku-input" />
            </div>
            <div>
              <Label>Warehouse</Label>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm((f) => ({ ...f, warehouse_id: v }))}>
                <SelectTrigger className="rounded-none mt-1" data-testid="raw-warehouse-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unit</Label>
              <Input className="rounded-none mt-1" value={form.unit} onChange={set("unit")} placeholder="kg / pcs / L" />
            </div>
            <div>
              <Label>Weight (per unit)</Label>
              <Input type="number" step="any" className="rounded-none mt-1" value={form.weight} onChange={set("weight", true)} />
            </div>
            <div>
              <Label>Quantity in stock</Label>
              <Input type="number" step="any" className="rounded-none mt-1" value={form.quantity} onChange={set("quantity", true)} data-testid="raw-qty-input" />
            </div>
            <div>
              <Label>Unit Cost</Label>
              <Input type="number" step="any" className="rounded-none mt-1" value={form.unit_cost} onChange={set("unit_cost", true)} />
            </div>
            <div>
              <Label>Reorder Level</Label>
              <Input type="number" step="any" className="rounded-none mt-1" value={form.reorder_level} onChange={set("reorder_level", true)} />
            </div>
            <div className="sm:col-span-2">
              <Label>Supplier (optional)</Label>
              <Input className="rounded-none mt-1" value={form.supplier} onChange={set("supplier")} />
            </div>
            <DialogFooter className="sm:col-span-2 mt-2">
              <Button type="submit" className="rounded-none" data-testid="save-raw-material-btn">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
